import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { autocompletion, completionKeymap, type Completion, type CompletionContext } from "@codemirror/autocomplete";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { html } from "@codemirror/lang-html";
import { json } from "@codemirror/lang-json";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { linter, type Diagnostic } from "@codemirror/lint";
import { Compartment, EditorState, type Extension } from "@codemirror/state";
import {
  Decoration, EditorView, keymap, MatchDecorator, placeholder as placeholderExt, ViewPlugin, type DecorationSet, type ViewUpdate,
} from "@codemirror/view";
import { tags } from "@lezer/highlight";
import { cn } from "@/lib/cn";
import type { VariableDeEvento } from "../../../services/notificaciones-service";
import { contextoAutocompletado, problemas, textoAlElegirVariable } from "../../../utils/plantilla-editor";

export type LenguajePlantilla = "texto" | "html" | "json";

export interface EditorPlantillaHandle {
  /** Inserta {{Variable}} en el cursor (botón "Variables"). */
  insertarVariable: (nombre: string) => void;
}

interface Props {
  valor: string;
  onCambio: (valor: string) => void;
  variables: VariableDeEvento[];
  formatos: string[];
  lenguaje?: LenguajePlantilla;
  multilinea?: boolean;
  filas?: number;
  placeholder?: string;
  etiquetaAccesible?: string;
}

/** Colores de sintaxis de la paleta (tonos medios, legibles en claro y oscuro). */
const resaltado = HighlightStyle.define([
  { tag: [tags.tagName, tags.angleBracket], color: "#0b5fff" },
  { tag: tags.attributeName, color: "#b95000" },
  { tag: [tags.attributeValue, tags.string], color: "#287d3c" },
  { tag: [tags.number, tags.bool, tags.null], color: "#9145ff" },
  { tag: tags.propertyName, color: "#b95000" },
  { tag: tags.comment, color: "#8f8f95", fontStyle: "italic" },
]);

const tema = EditorView.theme({
  "&": { fontSize: "12px", backgroundColor: "transparent" },
  "&.cm-focused": { outline: "none" },
  ".cm-content": { fontFamily: "var(--font-mono, ui-monospace, monospace)", padding: "6px 0", caretColor: "currentColor" },
  ".cm-line": { padding: "0 10px" },
  ".cm-scroller": { overflow: "auto" },
  ".cm-placeholder": { color: "var(--color-muted-foreground)" },
  // Variables {{…}}: válidas en naranja de marca; las inválidas las subraya el linter.
  ".cm-variable": { color: "var(--color-multiva-600)", backgroundColor: "color-mix(in srgb, var(--color-multiva-600) 10%, transparent)", borderRadius: "3px" },
  ".cm-tooltip": { border: "1px solid var(--color-border)", backgroundColor: "var(--color-popover, var(--color-card))", color: "var(--color-foreground)", borderRadius: "6px" },
  ".cm-tooltip-autocomplete > ul > li": { padding: "2px 8px !important" },
  ".cm-tooltip-autocomplete > ul > li[aria-selected]": { backgroundColor: "var(--color-muted)", color: "var(--color-foreground)" },
  ".cm-completionDetail": { marginLeft: "8px", fontStyle: "normal", color: "var(--color-muted-foreground)" },
  ".cm-completionInfo": { padding: "4px 8px", maxWidth: "260px" },
  ".cm-diagnostic": { padding: "4px 8px" },
});

/** Resalta cada {{…}} del texto. */
function resaltarVariables(): Extension {
  const decorador = new MatchDecorator({ regexp: /\{\{[^{}]*\}\}/g, decoration: Decoration.mark({ class: "cm-variable" }) });
  return ViewPlugin.fromClass(
    class {
      decoraciones: DecorationSet;
      constructor(view: EditorView) {
        this.decoraciones = decorador.createDeco(view);
      }
      update(u: ViewUpdate) {
        this.decoraciones = decorador.updateDeco(u, this.decoraciones);
      }
    },
    { decorations: (v) => v.decoraciones },
  );
}

/** Autocompletado y validación dependen de las variables del evento conectado: van en un Compartment. */
function extensionesDeVariables(variables: VariableDeEvento[], formatos: string[]): Extension {
  const fuente = (ctx: CompletionContext) => {
    const linea = ctx.state.doc.lineAt(ctx.pos);
    const c = contextoAutocompletado(linea.text.slice(0, ctx.pos - linea.from), linea.from);
    if (!c) return null;
    const despues = ctx.state.sliceDoc(ctx.pos, Math.min(linea.to, ctx.pos + 60));
    const opciones: Completion[] =
      c.tipo === "variable"
        ? variables.map((v) => ({
            label: v.nombre,
            detail: v.tipo,
            info: v.descripcion ?? undefined,
            type: v.origen === "evento" ? "property" : "variable",
            apply: textoAlElegirVariable(v.nombre, despues),
          }))
        : formatos.map((f) => ({ label: f, type: "function", detail: "formato", apply: /^\s*\}\}/.test(despues) || f === "sivacio" ? f : `${f}}}` }));
    return { from: c.desde, options: opciones, validFor: /^[\w.]*$/ };
  };

  const validar = (view: EditorView): Diagnostic[] =>
    problemas(view.state.doc.toString(), variables.map((v) => v.nombre), formatos).map((p) => ({
      from: p.desde, to: p.hasta, severity: "error", message: p.mensaje,
    }));

  return [
    autocompletion({ override: [fuente], activateOnTyping: true, icons: false }),
    // Sin variables (notificación sin evento conectado) no hay contra qué validar.
    variables.length > 0 ? linter(validar, { delay: 250 }) : [],
  ];
}

/** Una sola línea: los saltos de línea (tecleados o pegados) se vuelven espacios. */
const unaLinea = EditorState.transactionFilter.of((tr) => {
  if (!tr.docChanged || tr.newDoc.lines === 1) return tr;
  const texto = tr.newDoc.toString().replace(/\r?\n/g, " ");
  return [tr, { changes: { from: 0, to: tr.newDoc.length, insert: texto }, sequential: true }];
});

/**
 * Editor de plantillas estilo Scintilla (CodeMirror 6): resaltado de HTML/JSON y de las
 * variables, autocompletado al teclear <c>{{</c> (y formatos tras <c>|</c>), validación en vivo
 * con subrayado rojo y sin corrector ortográfico del navegador.
 */
export const EditorPlantilla = forwardRef<EditorPlantillaHandle, Props>(function EditorPlantilla(
  { valor, onCambio, variables, formatos, lenguaje = "texto", multilinea = false, filas = 4, placeholder, etiquetaAccesible },
  ref,
) {
  const host = useRef<HTMLDivElement>(null);
  const vista = useRef<EditorView | null>(null);
  const compartimentoVariables = useRef(new Compartment());
  const alCambiar = useRef(onCambio);
  alCambiar.current = onCambio;

  useImperativeHandle(ref, () => ({
    insertarVariable: (nombre) => {
      const v = vista.current;
      if (!v) return;
      const { from, to } = v.state.selection.main;
      const despues = v.state.sliceDoc(to, to + 60);
      const texto = `{{${textoAlElegirVariable(nombre, despues)}`;
      v.dispatch({ changes: { from, to, insert: texto }, selection: { anchor: from + texto.length } });
      v.focus();
    },
  }));

  // Montaje (una vez por lenguaje/modo): el resto se reconfigura sin recrear el editor.
  useEffect(() => {
    if (!host.current) return;
    const v = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: valor,
        extensions: [
          history(),
          keymap.of([...completionKeymap, ...defaultKeymap, ...historyKeymap]),
          multilinea ? EditorView.lineWrapping : unaLinea,
          lenguaje === "html" ? html() : lenguaje === "json" ? json() : [],
          syntaxHighlighting(resaltado),
          resaltarVariables(),
          compartimentoVariables.current.of(extensionesDeVariables(variables, formatos)),
          placeholder ? placeholderExt(placeholder) : [],
          EditorView.contentAttributes.of({
            spellcheck: "false", autocorrect: "off", autocapitalize: "off", "aria-label": etiquetaAccesible ?? "Plantilla",
          }),
          tema,
          EditorView.updateListener.of((u) => {
            if (u.docChanged) alCambiar.current(u.state.doc.toString());
          }),
        ],
      }),
    });
    vista.current = v;
    return () => {
      v.destroy();
      vista.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- valor/variables se sincronizan en los efectos de abajo
  }, [lenguaje, multilinea, placeholder]);

  // Variables del evento conectado cambian (se conecta/desconecta un evento).
  useEffect(() => {
    vista.current?.dispatch({ effects: compartimentoVariables.current.reconfigure(extensionesDeVariables(variables, formatos)) });
  }, [variables, formatos]);

  // Valor cambiado desde fuera (descartar cambios, otra notificación…).
  useEffect(() => {
    const v = vista.current;
    if (v && v.state.doc.toString() !== valor) v.dispatch({ changes: { from: 0, to: v.state.doc.length, insert: valor } });
  }, [valor]);

  return (
    <div
      ref={host}
      className={cn(
        "rounded-md border border-border bg-background focus-within:border-multiva-600",
        multilinea && "[&_.cm-editor]:max-h-80 [&_.cm-scroller]:min-h-[var(--min-h)]",
      )}
      style={multilinea ? ({ "--min-h": `${filas * 18 + 12}px` } as React.CSSProperties) : undefined}
    />
  );
});
